import * as Sentry from '@sentry/vue';
import type { Breadcrumb, ErrorEvent } from '@sentry/vue';
import type { RuntimeConfig } from '@ccex/shared';
import { retrieveLaunchParams } from '@tma.js/sdk-vue';
import type { App } from 'vue';
import type { Router } from 'vue-router';
import { classifyApiError } from './classify';
import { normalisePath } from './paths';
import { setMonitoringHooks } from './report';
import type { ApiFailure } from './report';

/**
 * The only file that imports `@sentry/*`. Errors only: no tracing, no replay, no feedback
 * (spec 2026-10-08 §3.2). PII rules of §3.5 are enforced in `beforeSend`/`beforeBreadcrumb`,
 * which also strip Vue component props and the URL fragment (path only) from navigation breadcrumbs.
 */

/** Known leak routes. An event carrying one is dropped whole rather than scrubbed. */
const LEAK_PATTERNS: readonly RegExp[] = [/initData=/, /tgWebAppData/, /\w@\w/];

export function beforeSend(event: ErrorEvent): ErrorEvent | null {
  // The URL may carry the Telegram hash fragment; headers carry the user agent. Neither is needed.
  delete event.request;
  delete event.extra; // we never set extra; a non-Error rejection would be serialised there
  if (event.user) {
    delete event.user.email;
    delete event.user.ip_address;
  }
  // Component props (amounts, recipients, emails) must never ship, even if attachProps is enabled.
  const vue = event.contexts?.vue;
  if (vue) delete vue.propsData;
  const texts = [event.message ?? '', ...(event.exception?.values ?? []).map((v) => v.value ?? '')];
  return texts.some((text) => LEAK_PATTERNS.some((pattern) => pattern.test(text))) ? null : event;
}

export function beforeBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  if (breadcrumb.category === 'console') return null;
  if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
    const data = breadcrumb.data ?? {};
    return {
      ...breadcrumb,
      data: {
        method: data.method,
        url: normalisePath(typeof data.url === 'string' ? data.url : ''),
        status_code: data.status_code,
      },
    };
  }
  if (breadcrumb.category === 'navigation' && breadcrumb.data) {
    // `from`/`to` are path + query + fragment, and the fragment carries Telegram init data.
    const data = { ...breadcrumb.data };
    for (const key of ['from', 'to']) {
      if (typeof data[key] === 'string') data[key] = normalisePath(data[key]);
    }
    return { ...breadcrumb, data };
  }
  return breadcrumb;
}

/** Device facts from the Telegram launch parameters; empty outside Telegram. */
function telegramTags(): Record<string, string> {
  try {
    const params = retrieveLaunchParams();
    return {
      'telegram.platform': String(params.tgWebAppPlatform),
      'telegram.version': String(params.tgWebAppVersion),
    };
  } catch {
    return {};
  }
}

function reportToSentry({ error, method, path }: ApiFailure, router: Router): void {
  const normalised = normalisePath(path);
  const classification = classifyApiError(error, { method, path: normalised });
  if (classification.kind === 'breadcrumb') {
    Sentry.addBreadcrumb({
      category: 'api',
      level: 'info',
      message: `${method} ${normalised} → ${error.status} ${error.code}`,
    });
    return;
  }
  const tags: Record<string, string> = {
    'api.method': method,
    'api.path': normalised,
    'api.status': String(error.status),
    'api.code': error.code,
    route: String(router.currentRoute.value.name ?? ''),
  };
  if (typeof error.extra.requestId === 'string') tags.request_id = error.extra.requestId;
  if (typeof error.extra.cfRay === 'string') tags.cf_ray = error.extra.cfRay;
  Sentry.withScope((scope) => {
    scope.setLevel(classification.level);
    scope.setTags(tags);
    scope.setFingerprint(classification.fingerprint);
    if (Array.isArray(error.extra.issues))
      scope.setContext('schema', { issues: error.extra.issues });
    Sentry.captureException(error);
  });
}

export function initMonitoring(
  app: App,
  router: Router,
  config: RuntimeConfig,
  locale: string,
): void {
  if (!config.sentryDsn) return;
  Sentry.init({
    app,
    dsn: config.sentryDsn,
    environment: config.environment,
    release: config.release,
    // v11 has no sendDefaultPii; its dataCollection defaults collect everything. Off, explicitly:
    // no IP inference, no cookies, no headers, no bodies, no query strings, no local variables.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      stackFrameVariables: false,
    },
    tracesSampleRate: 0,
    // Vue props carry amounts and recipient fields.
    attachProps: false,
    maxBreadcrumbs: 30,
    beforeSend,
    beforeBreadcrumb,
  });
  Sentry.setTags({ locale, ...telegramTags() });
  setMonitoringHooks({
    apiFailure: (failure) => reportToSentry(failure, router),
    user: (id) => Sentry.setUser(id ? { id } : null),
  });
}
